import { useCallback } from 'react';

import type { UseFormSetValue } from 'react-hook-form';

import type { GetPropertyTypesQuery } from '@graphql/__generated__/graphql';
import type { TransactionFormData } from '@modules/_shared/components/create-transaction/hooks/useTransactionStepper';
import {
  type ListingItem,
  listingToAddress,
  listingToImage,
  listingToPropertyData,
} from '@modules/_shared/utils/listingDataTransformers';

type PropertyTypes = GetPropertyTypesQuery['property_types'];

export const useListingFormPrefill = (
  setValue: UseFormSetValue<TransactionFormData>,
  propertyTypes: PropertyTypes | undefined,
) => {
  return useCallback(
    (listing: ListingItem) => {
      const listingAddress = listingToAddress(listing);

      if (listingAddress.route) {
        setValue('address.route', listingAddress.route);
      }
      if (listingAddress.street_number) {
        setValue('address.street_number', listingAddress.street_number);
      }
      if (listingAddress.postcode) {
        setValue('address.postcode', listingAddress.postcode);
      }
      if (listingAddress.locality) {
        setValue('address.locality', listingAddress.locality);
      }
      if (listingAddress.country_code) {
        setValue('address.country_code', listingAddress.country_code);
      }
      if (listingAddress.state) {
        setValue('address.state', listingAddress.state);
      }

      const propertyData = listingToPropertyData(listing, propertyTypes);
      if (propertyData) {
        setValue('property.composite_type', propertyData.composite_type ?? '');
        setValue('property.property_type_id', propertyData.property_type_id);
        setValue('property.number_of_rooms', propertyData.number_of_rooms);
        setValue(
          'property.number_of_bedrooms',
          propertyData.number_of_bedrooms,
        );
        setValue('property.living_surface', propertyData.living_surface);
        setValue('property.land_surface', propertyData.land_surface);
      }

      const imageData = listingToImage(listing);
      if (imageData) {
        setValue('photos.listingImage', imageData);
      }

      if (listing.id) {
        setValue('selectedListingId', Number(listing.id));
      }
    },
    [setValue, propertyTypes],
  );
};

export const hasAllRequiredAddressFields = (
  address: TransactionFormData['address'],
  listingAddress: ReturnType<typeof listingToAddress>,
): boolean => {
  const mergedAddress = { ...address, ...listingAddress };

  const requiredFields = [
    'street_number',
    'route',
    'locality',
    'postcode',
    'country_code',
    'country',
    'state',
  ] as const;

  return !requiredFields.some(field => !mergedAddress?.[field]);
};
